import { useCallback, useMemo } from "react";

import type { LangCode, Voucher_EditPageQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";
import { HeaderUpdateButtonAndInSaleSwitch } from "@/components/features/items/edit/_shared/ui/components/header/header-update-button-and-in-sale-switch";

import * as Shared from "../../_shared";
import {
	QuestionDescription,
	QuestionSalesTerm,
	QuestionSellingPrice,
	QuestionVoucherKeyVisual,
	QuestionVoucherName,
	QuestionVoucherPrice,
	QuestionVoucherUsageRestrictions,
} from "../../_shared";
import * as Model from "../model";

type Props = {
	hotelID: string;
	voucherID: string;

	queryData: Voucher_EditPageQuery;

	activeLangCode?: LangCode;
	navigator?: {
		default: string;
		i18n: (langCode: LangCode) => string;
	};
};

export const MarketingVoucherEditExistingPresenter = ({
	hotelID,
	voucherID,
	queryData,
	navigator,
	activeLangCode,
}: Props) => {
	/**
	 * 初期化
	 */
	const {
		viewModel,
		onSetViewModel,
		onChangeInSale,
		//
		onVoucherUpdate,
	} = Model.UseCase.useInitialized({ hotelID, voucherID, queryData });

	const { isDirty } = viewModel;
	useTransitionBlock(isDirty);

	/**
	 * 公開
	 */
	const onPublishForViewModel = useCallback(() => onChangeInSale(true), [onChangeInSale]);
	const onPublishCallback = useCallback(() => {
		onPublishForViewModel();
	}, [onPublishForViewModel]);
	/**
	 * 非公開
	 */
	const onUnPublishForViewModel = useCallback(() => onChangeInSale(false), [onChangeInSale]);
	const onUnPublishCallback = useCallback(() => {
		onUnPublishForViewModel();
	}, [onUnPublishForViewModel]);

	const handleChangeInSale = useCallback(() => {
		if (viewModel.inSale.value) {
			onUnPublishCallback();
		} else {
			onPublishCallback();
		}
	}, [onUnPublishCallback, onPublishCallback, viewModel.inSale.value]);

	const isValid = useMemo(() => !viewModel.hasError && viewModel.isDirty, [viewModel.hasError, viewModel.isDirty]);

	return (
		<Shared.Provider.MarketingVoucherEditContextProvider value={{ viewModel, onSetViewModel }}>
			<FeatureIslandContainer>
				<FeatureIslandHeader
					title="宿泊券編集"
					navigator={navigator}
					activeLangCode={activeLangCode}
					rightChildren={
						<HeaderUpdateButtonAndInSaleSwitch
							updateButton={{
								isValid,
								handleClick: onVoucherUpdate,
							}}
							inSaleSwitch={{
								inSale: viewModel.inSale,
								handleChangeInSale,
							}}
						/>
					}
				/>

				<FeatureIslandMainWithBackGround islandTitle="基本設定">
					<QuestionVoucherName />

					<QuestionVoucherKeyVisual ownerID={viewModel.hotelID} resourceID={viewModel.voucherID} />

					<QuestionDescription />

					<QuestionVoucherPrice />

					<QuestionSellingPrice />

					<QuestionSalesTerm />

					<QuestionVoucherUsageRestrictions />

					<Shared.QuestionMaxStockNum />
				</FeatureIslandMainWithBackGround>
			</FeatureIslandContainer>
		</Shared.Provider.MarketingVoucherEditContextProvider>
	);
};

MarketingVoucherEditExistingPresenter.displayName = "MarketingVoucherEditExistingPresenter";
